import { env } from 'config/env';
import config from 'config/constants';
import { isEnvironmentInApiKey } from 'app/utils';

const PRODUCT_URL = 'https://flybyjing.com/collections/shop/products/the-big-boi';

export const createTypescriptCodeExample = (apiKey = 'your_api_key_here') => {
  const clientOptionsString = isEnvironmentInApiKey(apiKey)
    ? `apiKey: "${apiKey}",`
    : `apiKey: "${apiKey}",
  environment: "${env.VITE_NODE_ENV}",`;

  return `import CheckoutIntents from 'checkout-intents';

const client = new CheckoutIntents({
  ${clientOptionsString}
});

// Step 1: Create checkout intent + poll until awaiting confirmation
const intent = await client.checkoutIntents.createAndPoll({
  productUrl: "${PRODUCT_URL}",
  quantity: 1,
  buyer: {
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@example.com",
    phone: "212-333-2121",
    address1: "123 Main St",
    city: "New York",
    province: "NY",
    postalCode: "10001",
    country: "US",
  },
});

// Step 2: Review offer details (price, shipping, taxes)
console.log("Offer:", intent.offer);

// Step 3: Confirm with payment + poll until completed
const completedIntent = await client.checkoutIntents.confirmAndPoll(intent.id, {
  paymentMethod: {
    type: "stripe_token",
    stripeToken: "tok_visa",
  },
});

// Step 4: Handle result
if (completedIntent.state === "completed") {
  console.log("Order placed successfully!", completedIntent);
} else if (completedIntent.state === "failed") {
  console.error("Order failed:", completedIntent.failureReason);
}`;
};

export const createPythonCodeExample = (apiKey = 'your_api_key_here') => {
  const clientOptionsString = isEnvironmentInApiKey(apiKey)
    ? `api_key="${apiKey}",`
    : `api_key="${apiKey}",
        environment="${env.VITE_NODE_ENV}",`;

  return `from checkout_intents import CheckoutIntents


def main():
    client = CheckoutIntents(
        ${clientOptionsString}
    )

    # Step 1: Create checkout intent + poll until awaiting confirmation
    intent = client.checkout_intents.create_and_poll(
        product_url="${PRODUCT_URL}",
        quantity=1,
        buyer={
            "first_name": "John",
            "last_name": "Doe",
            "email": "john.doe@example.com",
            "phone": "212-333-2121",
            "address1": "123 Main St",
            "city": "New York",
            "province": "NY",
            "postal_code": "10001",
            "country": "US",
        },
    )

    # Step 2: Review offer details (price, shipping, taxes)
    print("Offer:", intent.offer)

    # Step 3: Confirm with payment + poll until completed
    completed_intent = client.checkout_intents.confirm_and_poll(
        intent.id,
        payment_method={
            "type": "stripe_token",
            "stripe_token": "tok_visa",
        },
    )

    # Step 4: Handle result
    if completed_intent.state == "completed":
        print("Order placed successfully!", completed_intent)
    elif completed_intent.state == "failed":
        print("Order failed:", completed_intent.failure_reason)


if __name__ == "__main__":
    main()`;
};

export const createJavaCodeExample = (apiKey = 'your_api_key_here') => {
  const clientOptionsString = isEnvironmentInApiKey(apiKey)
    ? `.apiKey("${apiKey}")`
    : `.apiKey("${apiKey}")
            .environment("${env.VITE_NODE_ENV}")`;

  return `import com.rye.client.okhttp.CheckoutIntentsOkHttpClient
import com.rye.models.checkoutintents.Buyer
import com.rye.models.checkoutintents.CheckoutIntentConfirmParams
import com.rye.models.checkoutintents.CheckoutIntentCreateParams
import com.rye.models.checkoutintents.PaymentMethod

fun main() {
    val client =
        CheckoutIntentsOkHttpClient.builder()
            ${clientOptionsString}
            .build()

    // Step 1: Create checkout intent + poll until awaiting confirmation
    val intent =
        client
            .checkoutIntents()
            .createAndPoll(
                CheckoutIntentCreateParams.builder()
                    .productUrl(
                        "${PRODUCT_URL}"
                    )
                    .quantity(1)
                    .buyer(
                        Buyer.builder()
                            .firstName("John")
                            .lastName("Doe")
                            .email("john.doe@example.com")
                            .phone("212-333-2121")
                            .address1("123 Main St")
                            .city("New York")
                            .province("NY")
                            .postalCode("10001")
                            .country("US")
                            .build()
                    )
                    .build()
            )

    // Step 2: Review offer details (price, shipping, taxes)
    println("Offer: \${intent.asAwaitingConfirmation().offer()}")

    // Step 3: Confirm with payment + poll until completed
    val completedIntent =
        client
            .checkoutIntents()
            .confirmAndPoll(
                intent.asAwaitingConfirmation().id(),
                CheckoutIntentConfirmParams.builder()
                    .paymentMethod(
                        PaymentMethod.ofStripeToken(
                            PaymentMethod.StripeTokenPaymentMethod.builder()
                                .stripeToken("tok_visa")
                                .type(PaymentMethod.StripeTokenPaymentMethod.Type.STRIPE_TOKEN)
                                .build()
                        )
                    )
                    .build()
            )

    // Step 4: Handle result
    if (completedIntent.isCompleted()) {
        println("Order placed successfully! \${completedIntent}")
    } else if (completedIntent.isFailed()) {
        println("Order failed: \${completedIntent.asFailed().failureReason()}")
    }
}`;
};

export const createRubyCodeExample = (apiKey = 'your_api_key_here') => {
  const clientOptionsString = isEnvironmentInApiKey(apiKey)
    ? `api_key: "${apiKey}"`
    : `api_key: "${apiKey}",
  environment: "${env.VITE_NODE_ENV}"`;

  return `require "checkout_intents"

client = CheckoutIntents::Client.new(
  ${clientOptionsString}
)

# Step 1: Create checkout intent + poll until awaiting confirmation
intent = client.checkout_intents.create_and_poll(
  buyer: CheckoutIntents::Buyer.new(
    first_name: "John",
    last_name: "Doe",
    email: "john.doe@example.com",
    phone: "212-333-2121",
    address1: "123 Main St",
    city: "New York",
    province: "NY",
    postal_code: "10001",
    country: "US"
  ),
  product_url: "${PRODUCT_URL}",
  quantity: 1
)

# Step 2: Review offer details (price, shipping, taxes)
puts "Offer: #{intent.offer}"

# Step 3: Confirm with payment + poll until completed
completed_intent = client.checkout_intents.confirm_and_poll(
  intent.id,
  payment_method: CheckoutIntents::PaymentMethod::StripeTokenPaymentMethod.new(
    type: :stripe_token,
    stripe_token: "tok_visa"
  )
)

# Step 4: Handle result
case completed_intent
when CheckoutIntents::CheckoutIntent::CompletedCheckoutIntent
  puts "Order placed successfully! #{completed_intent}"
when CheckoutIntents::CheckoutIntent::FailedCheckoutIntent
  puts "Order failed: #{completed_intent.failure_reason}"
end`;
};

export const createCurlCodeExample = (apiKey = 'your_api_key_here') => {
  const baseUrl = config.external.checkoutIntent;

  return `# Step 1: Create a checkout intent
curl --request POST \\
  --url ${baseUrl} \\
  --header "Authorization: Bearer ${apiKey}" \\
  --header 'Content-Type: application/json' \\
  --data '{
    "buyer": {
      "postalCode": "10001",
      "country": "US",
      "province": "NY",
      "city": "New York",
      "address1": "123 Main St",
      "phone": "212-333-2121",
      "email": "john.doe@example.com",
      "lastName": "Doe",
      "firstName": "John"
    },
    "productUrl": "${PRODUCT_URL}",
    "quantity": "1"
  }'

# Step 2: Poll until state is "awaiting_confirmation"
curl --request GET \\
  --url ${baseUrl}/{id} \\
  --header "Authorization: Bearer ${apiKey}"

# Step 3: After you've reviewed the retrieved offer, confirm the checkout intent with payment
curl --request POST \\
  --url ${baseUrl}/{id}/confirm \\
  --header "Authorization: Bearer ${apiKey}" \\
  --header 'Content-Type: application/json' \\
  --data '{
    "paymentMethod": {
      "stripeToken": "tok_visa",
      "type": "stripe_token"
    }
  }'

# Step 4: Poll until state is "completed" or "failed"
curl --request GET \\
  --url ${baseUrl}/{id} \\
  --header "Authorization: Bearer ${apiKey}"`;
};
